import { useCallback, useRef, useState, type ReactNode } from 'react'
import { ConfirmDialog } from './ConfirmDialog'

export interface ConfirmRequest {
  title: string
  /** What will happen, in plain words; name the object ("Document ‘pitch.pdf’ …"). */
  description?: ReactNode
  confirmLabel?: string
  /** `false` (with an own `confirmLabel`) when nothing is deleted. */
  destructive?: boolean
}

/**
 * `window.confirm`, but as the platform's `ConfirmDialog` (spec-64-u2, U-098 / IA-12):
 * `if (!(await confirm({ title: 'Document verwijderen?' }))) return`. Render the returned
 * dialog once in the component. Escape, the scrim and "Annuleren" resolve to `false`.
 */
export function useConfirm(): [ReactNode, (request: ConfirmRequest) => Promise<boolean>] {
  const [open, setOpen] = useState(false)
  // The last request stays on screen while the dialog animates out.
  const [shown, setShown] = useState<ConfirmRequest>({ title: '' })
  const resolver = useRef<((ok: boolean) => void) | null>(null)

  const confirm = useCallback((request: ConfirmRequest) => {
    resolver.current?.(false)
    setShown(request)
    setOpen(true)
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve
    })
  }, [])

  const settle = (ok: boolean) => {
    resolver.current?.(ok)
    resolver.current = null
    setOpen(false)
  }

  const dialog = (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) settle(false)
      }}
      title={shown.title}
      description={shown.description}
      confirmLabel={shown.confirmLabel}
      destructive={shown.destructive}
      onConfirm={() => settle(true)}
    />
  )
  return [dialog, confirm]
}
